import React from 'react';
import './knkt-background.css';

interface KnktBackgroundProps {
  /** Show the noise texture overlay */
  showNoise?: boolean;
  /** Show the grid overlay */
  showGrid?: boolean;
  /** Custom className for additional styling */
  className?: string;
  /** Children content to render on top of the background */
  children?: React.ReactNode;
}

/**
 * KNKT Background Component
 *
 * A reusable animated mesh gradient background with floating orbs,
 * optional noise texture, and grid overlay. This is the official
 * KNKT platform background that provides the signature cyan-purple-pink
 * shader effect.
 *
 * @example
 * // Basic usage - full page background
 * <KnktBackground>
 *   <div className="your-content">Content here</div>
 * </KnktBackground>
 *
 * @example
 * // Without noise and grid overlays
 * <KnktBackground showNoise={false} showGrid={false}>
 *   <YourApp />
 * </KnktBackground>
 */
export function KnktBackground({
  showNoise = true,
  showGrid = true,
  className = '',
  children
}: KnktBackgroundProps) {
  return (
    <div className={`knkt-background-container ${className}`}>
      {/* Shader Background */}
      <div className="knkt-background">
        <div className="knkt-orb knkt-orb-1"></div>
        <div className="knkt-orb knkt-orb-2"></div>
        <div className="knkt-orb knkt-orb-3"></div>
        <div className="knkt-orb knkt-orb-4"></div>
        <div className="knkt-orb knkt-orb-5"></div>
        {showNoise && <div className="knkt-noise-overlay"></div>}
        {showGrid && <div className="knkt-grid-overlay"></div>}
      </div>

      {/* Content */}
      {children && (
        <div className="knkt-background-content">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Standalone background orbs for use in custom layouts
 * Use this if you need more control over the background structure
 */
export function KnktBackgroundOrbs() {
  return (
    <>
      <div className="knkt-orb knkt-orb-1"></div>
      <div className="knkt-orb knkt-orb-2"></div>
      <div className="knkt-orb knkt-orb-3"></div>
      <div className="knkt-orb knkt-orb-4"></div>
      <div className="knkt-orb knkt-orb-5"></div>
    </>
  );
}

export default KnktBackground;
